import { TERMINOLOGY } from '@shared/i18n/terminology';
import { useEffect, useMemo } from 'react';
import { ClipboardTaskListLtr20Regular } from '@fluentui/react-icons';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledField } from '@shared/components/ControlledField';
import { FormDialog } from '@shared/components/dialogs/FormDialog';
import { useUsers } from '@modules/users/api/users.api';
import { StatusLabel } from '@shared/components/StatusLabel';
import { dateToYMD } from '@shared/utils/date';
import { useInvalidDateInputs } from '@shared/hooks/useInvalidDateInputs.hook';
import { INVALID_DATE_MESSAGE } from '@shared/schemas/calendarDate.schema';
import type { TProject, TProjectFormValues, TUpdateProjectDetails } from '../types';
import { projectFormSchema, DEFAULT_PROJECT_FORM } from './projectForm';

type TProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  rootItemName: string;
  project?: TProject;
  currentUserId: string;
  canEditTeam?: boolean;
  onSave: (values: TProjectFormValues, changes: TUpdateProjectDetails) => Promise<unknown>;
};

const ProjectDialog = ({
  isOpen,
  setIsOpen,
  rootItemName,
  project,
  currentUserId,
  canEditTeam = true,
  onSave,
}: TProps) => {
  const { userMap, error: usersError } = useUsers();
  const { hasInvalidDateInput, onInvalidDateInput, resetInvalidDateInputs } = useInvalidDateInputs();
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<TProjectFormValues>({
    resolver: yupResolver(projectFormSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_PROJECT_FORM,
  });
  const excludedLeads = useMemo(
    () =>
      Object.values(userMap ?? {})
        .filter((user) => user.license !== 'FULL')
        .map((user) => user._id),
    [userMap]
  );
  const leads = useWatch({ control, name: 'leads' });

  useEffect(() => {
    const members = getValues('members');
    if (members.some((id) => leads.includes(id)))
      setValue(
        'members',
        members.filter((id) => !leads.includes(id)),
        { shouldDirty: true }
      );
  }, [leads]);

  useEffect(() => {
    if (!isOpen) return;
    resetInvalidDateInputs();
    reset(
      project
        ? {
            name: project.name,
            description: project.description ?? '',
            leads: project.leads,
            members: project.members,
            startDate: dateToYMD(project.startDate) || null,
            dueDate: dateToYMD(project.dueDate) || null,
          }
        : { ...DEFAULT_PROJECT_FORM, leads: [currentUserId] }
    );
  }, [isOpen, project, currentUserId, reset, resetInvalidDateInputs]);

  const onSubmit = async (values: TProjectFormValues) => {
    if (hasInvalidDateInput || !userMap || usersError) return;
    try {
      const changes: TUpdateProjectDetails = Object.fromEntries(
        Object.keys(dirtyFields).map((name) => [name, values[name as keyof TProjectFormValues]])
      );
      if (changes.description === '') changes.description = null;
      await onSave(values, changes);
      setIsOpen(false);
    } catch {
      /* The API hook reports the failure. Preserve the form for retry. */
    }
  };

  return (
    <FormDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        <div className="flex items-center space-x-2">
          <ClipboardTaskListLtr20Regular className="shrink-0" />
          <span className="min-w-0 break-words">
            {project ? `Edit ${TERMINOLOGY.project.lower}` : `Create ${TERMINOLOGY.project.lower} for ${rootItemName}`}
          </span>
        </div>
      }
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={project ? 'Save' : 'Create'}
      submitIcon={null}
      isSubmitting={isSubmitting}
      submitDisabled={!userMap || !!usersError || hasInvalidDateInput}
      contentClassName="max-h-[70vh] overflow-y-auto"
    >
      <StatusLabel
        data={userMap}
        error={usersError}
        errorMessage="Loading users failed!"
        render={() => (
          <div className="flex flex-col gap-4">
            <ControlledField
              inputType="input"
              name="name"
              label="Name"
              placeholder={`Enter ${TERMINOLOGY.project.lower} name`}
              required
              control={control}
              errors={errors}
            />
            <ControlledField
              inputType="textarea"
              name="description"
              label="Description"
              placeholder="Add a description"
              rows={4}
              resize="vertical"
              control={control}
              errors={errors}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ControlledField
                inputType="date"
                clearable
                errors={errors}
                rules={{ deps: ['dueDate'] }}
                name="startDate"
                label="Start date"
                valueFormat="day"
                control={control}
                onInvalidInputChange={onInvalidDateInput('startDate')}
              />
              <ControlledField
                inputType="date"
                clearable
                errors={errors}
                name="dueDate"
                label="Due date"
                valueFormat="day"
                control={control}
                onInvalidInputChange={onInvalidDateInput('dueDate')}
              />
            </div>
            {hasInvalidDateInput && <p className="text-red-500">{INVALID_DATE_MESSAGE}</p>}
            <ControlledField
              inputType="multiuserselect"
              name="leads"
              label={`${TERMINOLOGY.project.upper} leads`}
              required
              disabled={!canEditTeam}
              excludedUserIds={excludedLeads}
              control={control}
              errors={errors}
            />
            <ControlledField
              inputType="multiuserselect"
              name="members"
              label={`${TERMINOLOGY.project.upper} members`}
              disabled={!canEditTeam}
              excludedUserIds={leads}
              control={control}
              errors={errors}
            />
          </div>
        )}
      />
    </FormDialog>
  );
};

export default ProjectDialog;
